import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import ExchangesApi from '@/infrastructure/api/exchanges'
import { returnConditionsApi } from '@/infrastructure/api/returns-and-refunds'
import { ExchangePolicy } from '@/domain/exchanges'
import { GroupReturnConditions } from '@/domain/return-conditions'
import { Layered, dependentLayers } from '@/domain/group-settings'

const isFullCatalog = (policy: ExchangePolicy) => policy.exchangeFlow === 'FULL'

// Full Catalog exchanges let customers pay for a pricier item, so they need payments: turning payments off moves
// them to same-product exchanges, and choosing Full Catalog turns payments on, wherever the save reaches.
export function useFullCatalogPayments(group: string, stores: string[]) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const load = async () => {
    const [conditions, exchanges] = await Promise.all([
      returnConditionsApi.find(group),
      ExchangesApi.findGroupExchanges(group)
    ])
    return { conditions, policy: exchanges.policy }
  }
  const save = async <T>(
    layers: (string | undefined)[],
    layered: Layered<T>,
    next: (value: T) => T,
    saveGroup: (value: T) => Promise<void>,
    saveStore: (ecommerceId: string, value: T) => Promise<void>
  ) => {
    await Promise.all(
      layers.map((store) => {
        const value = (store && layered.overrides[store]) ?? layered.group
        if (!value) return
        return store ? saveStore(store, next(value)) : saveGroup(next(value))
      })
    )
    return layers.length > 0
  }

  // Where turning payments off for the group, or for one portal, would take Full Catalog away.
  const fullCatalogLayers = async (ecommerceId?: string) => {
    const { conditions, policy } = await load()
    return {
      policy,
      layers: dependentLayers(
        conditions,
        policy,
        stores,
        ecommerceId,
        isFullCatalog
      )
    }
  }

  const switchFullCatalogOff = async (ecommerceId?: string) => {
    const { policy, layers } = await fullCatalogLayers(ecommerceId)
    const saved = await save<ExchangePolicy>(
      layers,
      policy,
      (value) => ({ ...value, exchangeFlow: 'ONE_ON_ONE' }),
      (value) => ExchangesApi.saveGroupExchanges(group, 'policy', value),
      (store, value) => ExchangesApi.saveStoreExchanges(store, 'policy', value)
    )
    if (saved)
      queryClient.invalidateQueries({ queryKey: ['groupExchanges', group] })
  }

  const turnPaymentsOn = async (ecommerceId?: string) => {
    const { conditions, policy } = await load()
    const layers = dependentLayers(
      policy,
      conditions,
      stores,
      ecommerceId,
      (value) => !value.paymentsEnabled
    )
    const saved = await save<GroupReturnConditions>(
      layers,
      conditions,
      (value) => ({ ...value, paymentsEnabled: true }),
      (value) => returnConditionsApi.saveGroup(group, value),
      (store, value) => returnConditionsApi.saveStore(store, value)
    )
    if (saved) {
      toast({
        text: t('exchanges.policy.payments_turned_on'),
        variant: 'success'
      })
      queryClient.invalidateQueries({
        queryKey: ['groupSettings', 'returnConditions', group]
      })
    }
  }

  return { fullCatalogLayers, switchFullCatalogOff, turnPaymentsOn }
}
